import type RootStore from "stores/RootStore";
import { RouteType } from "types/RouteType";
import { throwIfFailed } from "shared/dialogSubmit";
import ClientInstancesStore from "./ClientInstancesStore";
import InstanceActionsStore from "../InstanceActions/InstanceActionsStore";

const clientInstancesRoute: RouteType[] = [
  {
    name: "Client Instances",
    path: "/infrastructure/client-instances",
    layout: "/app",
    viewportCard: {
      header: {
        enableBackButton: true,
        title: "Client Instances",
        enableRefreshButton: true,
        component: () => import("./ClientInstances.Header"),
      },
      body: {
        component: () => import("./ClientInstances.Body"),
        grayBackground: true,
      },
    },
    group: "Infrastructure",
    searchAliases: ["deployments", "hosted erp", "instances"],
    // Reads require authentication only (viewer+); the API enforces the read
    // gate (rbac.ts viewer/operator/admin seam). Start/Stop/Restart/Deploy/
    // Rollback are operator+ — gated in the store (`canManage`) and re-enforced
    // by the API. The Portainer host key is never sent/returned.
    requiresAuth: true,
    requiresPermission: false,
    onEnter: ({ rootStore }) => {
      const store = rootStore.clientInstancesViewStore as ClientInstancesStore;
      store?.initialize();
    },
    onExit: ({ rootStore }) => {
      // Drop the live fleet-status listener so it doesn't keep patching state
      // while the grid is unmounted. Idempotent; rebinds on next onEnter.
      const store = rootStore.clientInstancesViewStore as ClientInstancesStore;
      store?.dispose();
    },
    dialogs: [
      {
        name: "InstanceDeployDialog",
        title: "Deploy Image",
        component: () => import("../InstanceActions/dialogs/DeployDialog.Body"),
        cancelButton: true,
        height: 420,
        submitButtonText: "Deploy",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          const grid = rootStore.clientInstancesViewStore as ClientInstancesStore;
          // A refused deploy throws: the dialog stays open with the reason
          // in its footer (shared/dialogSubmit).
          throwIfFailed(await actions.submitDeploy(), actions.deployError, "The image could not be deployed.");
          await grid.reload();
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          return actions.isDeployValid && actions.canManage;
        },
      },
      {
        name: "InstanceRollbackDialog",
        title: "Roll Back Image",
        component: () =>
          import("../InstanceActions/dialogs/RollbackDialog.Body"),
        cancelButton: true,
        height: 520,
        submitButtonText: "Roll Back",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          const grid = rootStore.clientInstancesViewStore as ClientInstancesStore;
          // A refused rollback throws: the dialog stays open with the reason
          // in its footer (shared/dialogSubmit).
          throwIfFailed(await actions.submitRollback(), actions.rollbackError, "The rollback could not be started.");
          await grid.reload();
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          return actions.isRollbackValid && actions.canManage;
        },
      },
      {
        name: "InstanceConfirmActionDialog",
        title: "Confirm Action",
        component: () =>
          import("../InstanceActions/dialogs/ConfirmActionDialog.Body"),
        cancelButton: true,
        // Tall enough for the progress panel the body shows once Confirm is tapped.
        height: 440,
        submitButtonText: "Confirm",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          const grid = rootStore.clientInstancesViewStore as ClientInstancesStore;
          // Run the confirmed action through the grid store so its optimistic
          // status + refresh path runs. 'remove' targets the cache row (dead
          // container) and has its own runner; everything else is a container
          // lifecycle action.
          // 'update-to-latest' is server-resolved (the API picks the digest),
          // so it has its own runner rather than going through the generic
          // lifecycle transport — it reaches this dialog because the audit
          // found Update firing on one tap with no confirmation at all.
          await actions.runConfirmed(async (instance, action, progressToken) => {
            if (action === "remove") return grid.removeInstance(instance);
            if (action === "update-to-latest") {
              const ok = await actions.updateToLatest(instance);
              if (ok) await grid.reload();
              return ok;
            }
            return grid.runLifecycleAction(instance, action, progressToken);
          });
        },
      },
      {
        name: "AssignInstanceDialog",
        title: "Assign to Client",
        component: () => import("./dialogs/AssignInstanceDialog.Body"),
        cancelButton: true,
        height: 300,
        submitButtonText: "Assign",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          // A refused assign throws: the dialog stays open with the reason in
          // its footer (shared/dialogSubmit).
          const grid = rootStore.clientInstancesViewStore as ClientInstancesStore;
          throwIfFailed(await grid.submitAssign(), grid.assignError, "The container could not be assigned.");
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const grid = rootStore.clientInstancesViewStore as ClientInstancesStore;
          return grid.isAssignValid && grid.canManage;
        },
      },
      {
        // Move a container to ANOTHER client: recreated with the new client
        // label (it restarts). Shared body with the InstanceDetail route.
        name: "InstanceReassignDialog",
        title: "Move to Client",
        component: () => import("../InstanceActions/dialogs/ReassignDialog.Body"),
        cancelButton: true,
        height: 520,
        submitButtonText: "Recreate & move",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          const grid = rootStore.clientInstancesViewStore as ClientInstancesStore;
          const ok = await actions.submitReassign();
          if (ok) await grid.reload();
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          return actions.isReassignValid && actions.canManage;
        },
      },
    ],
  },
];

export default clientInstancesRoute;
